/**
 * Step1BusinessInfo
 * Business information: name, legal name, BN, sector, province, city,
 * year established, employee count, description, logo upload.
 */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import FileUpload from '../../components/FileUpload';

const SECTORS = [
  { value: '', label: 'Select a sector...' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'telecom', label: 'Telecom' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'finance', label: 'Finance' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'home-services', label: 'Home Services' },
  { value: 'retail', label: 'Retail' },
  { value: 'travel', label: 'Travel' },
  { value: 'legal', label: 'Legal' },
  { value: 'education', label: 'Education' },
];

const PROVINCES = [
  { value: '', label: 'Select province...' },
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

const EMPLOYEE_RANGES = [
  { value: '', label: 'Select range...' },
  { value: '1-5', label: '1-5 employees' },
  { value: '6-25', label: '6-25 employees' },
  { value: '26-100', label: '26-100 employees' },
  { value: '101-500', label: '101-500 employees' },
  { value: '501-1000', label: '501-1,000 employees' },
  { value: '1000+', label: '1,000+ employees' },
];

const CURRENT_YEAR = new Date().getFullYear();

export function validateStep1(data) {
  const errors = {};
  if (!data.businessName?.trim()) errors.businessName = 'Business name is required';
  if (!data.sector) errors.sector = 'Please select a sector';
  if (!data.province) errors.province = 'Please select a province';
  if (!data.city?.trim()) errors.city = 'City is required';
  if (data.businessNumber && !/^\d{9}(RR\d{4})?$/.test(data.businessNumber.replace(/\s/g, ''))) {
    errors.businessNumber = 'Business Number must be 9 digits (optionally followed by RR and 4 digits)';
  }
  if (data.yearEstablished) {
    const year = parseInt(data.yearEstablished, 10);
    if (isNaN(year) || year < 1800 || year > CURRENT_YEAR) {
      errors.yearEstablished = `Year must be between 1800 and ${CURRENT_YEAR}`;
    }
  }
  if (data.description && data.description.length > 500) {
    errors.description = 'Description must be 500 characters or less';
  }
  return errors;
}

export default function Step1BusinessInfo({ data, onChange, errors, touched, onBlur }) {
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    onChange({ ...data, [name]: value });
  }, [data, onChange]);

  const handleLogoChange = useCallback((file) => {
    onChange({ ...data, logoFile: file });
  }, [data, onChange]);

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={sectionTitleStyle}>Business Information</h2>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          Tell us about your business. This information will appear on your public listing.
        </p>
      </div>

      {/* Business name and Legal name */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 280px' }}>
          <label style={labelStyle}>
            Business Name <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="text"
            name="businessName"
            value={data.businessName || ''}
            onChange={handleChange}
            onBlur={() => onBlur('businessName')}
            style={{ ...inputStyle, borderColor: touched.businessName && errors.businessName ? '#ef4444' : '#d1d5db' }}
            placeholder="e.g., Acme Solutions Inc."
          />
          {touched.businessName && errors.businessName && <div style={errorStyle}>{errors.businessName}</div>}
        </div>
        <div style={{ flex: '1 1 280px' }}>
          <label style={labelStyle}>Legal Name</label>
          <input
            type="text"
            name="legalName"
            value={data.legalName || ''}
            onChange={handleChange}
            style={inputStyle}
            placeholder="Legal entity name (if different)"
          />
        </div>
      </div>

      {/* Business Number and Sector */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>Business Number (BN)</label>
          <input
            type="text"
            name="businessNumber"
            value={data.businessNumber || ''}
            onChange={handleChange}
            onBlur={() => onBlur('businessNumber')}
            style={{ ...inputStyle, borderColor: touched.businessNumber && errors.businessNumber ? '#ef4444' : '#d1d5db' }}
            placeholder="123456789"
          />
          {touched.businessNumber && errors.businessNumber && <div style={errorStyle}>{errors.businessNumber}</div>}
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>
            9-digit CRA Business Number
          </div>
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>
            Sector <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <select
            name="sector"
            value={data.sector || ''}
            onChange={handleChange}
            onBlur={() => onBlur('sector')}
            style={{ ...inputStyle, cursor: 'pointer', borderColor: touched.sector && errors.sector ? '#ef4444' : '#d1d5db' }}
          >
            {SECTORS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {touched.sector && errors.sector && <div style={errorStyle}>{errors.sector}</div>}
        </div>
      </div>

      {/* Province and City */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>
            Province <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <select
            name="province"
            value={data.province || ''}
            onChange={handleChange}
            onBlur={() => onBlur('province')}
            style={{ ...inputStyle, cursor: 'pointer', borderColor: touched.province && errors.province ? '#ef4444' : '#d1d5db' }}
          >
            {PROVINCES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          {touched.province && errors.province && <div style={errorStyle}>{errors.province}</div>}
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>
            City <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="text"
            name="city"
            value={data.city || ''}
            onChange={handleChange}
            onBlur={() => onBlur('city')}
            style={{ ...inputStyle, borderColor: touched.city && errors.city ? '#ef4444' : '#d1d5db' }}
            placeholder="Toronto"
          />
          {touched.city && errors.city && <div style={errorStyle}>{errors.city}</div>}
        </div>
      </div>

      {/* Year established and Employees */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>Year Established</label>
          <input
            type="number"
            name="yearEstablished"
            value={data.yearEstablished || ''}
            onChange={handleChange}
            onBlur={() => onBlur('yearEstablished')}
            style={{ ...inputStyle, borderColor: touched.yearEstablished && errors.yearEstablished ? '#ef4444' : '#d1d5db' }}
            placeholder={String(CURRENT_YEAR)}
            min="1800"
            max={CURRENT_YEAR}
          />
          {touched.yearEstablished && errors.yearEstablished && <div style={errorStyle}>{errors.yearEstablished}</div>}
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>Number of Employees</label>
          <select
            name="employeeRange"
            value={data.employeeRange || ''}
            onChange={handleChange}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            {EMPLOYEE_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
      </div>

      {/* Description */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Business Description</label>
        <textarea
          name="description"
          value={data.description || ''}
          onChange={handleChange}
          onBlur={() => onBlur('description')}
          rows={4}
          maxLength={500}
          style={{
            ...inputStyle,
            resize: 'vertical',
            fontFamily: 'inherit',
            borderColor: touched.description && errors.description ? '#ef4444' : '#d1d5db',
          }}
          placeholder="Describe your business, services, and what makes you unique..."
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px' }}>
          {touched.description && errors.description
            ? <div style={errorStyle}>{errors.description}</div>
            : <span />
          }
          <span style={{ fontSize: '11px', color: (data.description || '').length > 450 ? '#f59e0b' : '#94a3b8' }}>
            {(data.description || '').length}/500
          </span>
        </div>
      </div>

      {/* Logo upload */}
      <div>
        <FileUpload
          label="Business Logo"
          accept="image"
          file={data.logoFile || null}
          onChange={handleLogoChange}
          helperText="Upload your company logo. Recommended: square image, at least 200x200px."
        />
      </div>
    </div>
  );
}

Step1BusinessInfo.propTypes = {
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  onBlur: PropTypes.func.isRequired,
};

const sectionTitleStyle = {
  fontSize: '20px',
  fontWeight: '700',
  color: '#1e293b',
  marginBottom: '6px',
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '4px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '14px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  backgroundColor: '#fff',
  color: '#1e293b',
  outline: 'none',
  boxSizing: 'border-box',
};

const errorStyle = {
  fontSize: '12px',
  color: '#ef4444',
  marginTop: '3px',
};
